/**
 * Treat — full-screen product image viewer.
 *
 * Clicking a product image in the gallery opens a full-screen dialog with every image the
 * gallery is currently showing (it respects the variant filter), starting at the one clicked.
 * Swipe or use the arrows / arrow keys to move between images; on a pointer device a click
 * zooms to 2x at that point and moving the pointer pans; phones pinch-zoom natively. Esc or the
 * close button returns focus to the image that opened it.
 */
if (!customElements.get('treat-media-zoom')) {
  customElements.define(
    'treat-media-zoom',
    class TreatMediaZoom extends HTMLElement {
      connectedCallback() {
        this.gallery = document.getElementById(this.dataset.gallery);
        this.dialog = this.querySelector('dialog');
        this.track = this.querySelector('[data-zoom-track]');
        this.counter = this.querySelector('[data-zoom-counter]');
        if (!this.gallery || !this.dialog || !this.track) return;

        this.gallery.addEventListener('click', (event) => {
          const img = event.target.closest('.product__media-item .product__modal-opener--image img');
          if (!img || !this.gallery.contains(img)) return;
          event.preventDefault();
          this.open(img);
        });

        this.querySelector('[data-zoom-close]').addEventListener('click', () => this.close());
        this.querySelector('[data-zoom-prev]').addEventListener('click', () => this.step(-1));
        this.querySelector('[data-zoom-next]').addEventListener('click', () => this.step(1));
        this.dialog.addEventListener('cancel', (event) => {
          event.preventDefault();
          this.close();
        });
        this.dialog.addEventListener('keydown', (event) => {
          const rtl = document.documentElement.dir === 'rtl';
          if (event.key === 'ArrowRight') this.step(rtl ? -1 : 1);
          if (event.key === 'ArrowLeft') this.step(rtl ? 1 : -1);
        });
        this.track.addEventListener('scroll', () => {
          window.cancelAnimationFrame(this.frame);
          this.frame = window.requestAnimationFrame(() => this.updateCounter());
        });
      }

      largeSrc(img) {
        try {
          const url = new URL(img.currentSrc || img.src, window.location.href);
          url.searchParams.set('width', '2400');
          return url.toString();
        } catch (error) {
          return img.currentSrc || img.src;
        }
      }

      visibleImages() {
        return Array.from(
          this.gallery.querySelectorAll(
            '.product__media-list .product__media-item:not(.hidden) .product__modal-opener--image img'
          )
        );
      }

      open(clicked) {
        const images = this.visibleImages();
        this.opener = clicked;
        this.track.replaceChildren(
          ...images.map((img) => {
            const slide = document.createElement('div');
            slide.className = 'treat-zoom__slide';
            const large = document.createElement('img');
            large.src = this.largeSrc(img);
            large.alt = img.alt || '';
            large.className = 'treat-zoom__image';
            large.draggable = false;
            this.bindZoom(slide, large);
            slide.appendChild(large);
            return slide;
          })
        );
        this.dialog.showModal();
        document.body.classList.add('overflow-hidden');
        const index = Math.max(0, images.indexOf(clicked));
        this.track.children[index]?.scrollIntoView({ block: 'nearest', inline: 'start', behavior: 'instant' });
        this.updateCounter();
      }

      close() {
        this.dialog.close();
        document.body.classList.remove('overflow-hidden');
        this.track.replaceChildren();
        this.opener?.focus?.();
      }

      currentIndex() {
        const width = this.track.clientWidth || 1;
        return Math.round(Math.abs(this.track.scrollLeft) / width);
      }

      step(delta) {
        const slides = this.track.children;
        if (!slides.length) return;
        const next = Math.min(slides.length - 1, Math.max(0, this.currentIndex() + delta));
        slides[next].scrollIntoView({ block: 'nearest', inline: 'start', behavior: 'smooth' });
      }

      updateCounter() {
        if (!this.counter) return;
        const total = this.track.children.length;
        this.counter.textContent = total ? `${this.currentIndex() + 1} / ${total}` : '';
      }

      bindZoom(slide, img) {
        if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
        slide.addEventListener('click', (event) => {
          const zoomed = slide.classList.toggle('is-zoomed');
          if (zoomed) this.pan(slide, img, event);
          else img.style.transformOrigin = '';
        });
        slide.addEventListener('mousemove', (event) => {
          if (slide.classList.contains('is-zoomed')) this.pan(slide, img, event);
        });
      }

      pan(slide, img, event) {
        const rect = slide.getBoundingClientRect();
        const x = ((event.clientX - rect.left) / rect.width) * 100;
        const y = ((event.clientY - rect.top) / rect.height) * 100;
        img.style.transformOrigin = `${x}% ${y}%`;
      }
    }
  );
}
